"use client";

import { X } from "lucide-react";
import { useEffect, useState } from "react";
import Link from "@/components/ui/Link";
import { useLang } from "@/components/ui/lang-context";
import { useUi } from "@/components/ui/use-ui";
import { fmt } from "@/lib/locales/fmt";
import { SweetCoin } from "./SweetCoin";

// A visitor without an account who has collected daily coins (lib/coins/guest.ts): their coins in the header like a
// member's, marked as not saved, and once a day a reminder when some of them will be lost within a day, with a
// way to sign up and keep them.

const SEEN_KEY = "sweet-guest-coins-reminded";

export function GuestCoins({ balance, expiring }: { balance: number; expiring: { amount: number; soon: boolean } | null }) {
  const t = useUi();
  const lang = useLang();
  const [remind, setRemind] = useState(false);
  const soon = expiring?.soon ? expiring : null;

  useEffect(() => {
    if (!soon) return;
    let seen: string | null = null;
    try {
      seen = localStorage.getItem(SEEN_KEY);
    } catch {
      // No storage (a private window): the reminder shows on each page, still closable.
    }
    // Once a day, not on every page.
    const today = new Date().toISOString().slice(0, 10);
    if (seen === today) return;
    const timer = window.setTimeout(() => setRemind(true), 2500);
    return () => window.clearTimeout(timer);
  }, [soon]);

  const close = () => {
    setRemind(false);
    try {
      localStorage.setItem(SEEN_KEY, new Date().toISOString().slice(0, 10));
    } catch {}
  };

  const full = new Intl.NumberFormat(lang).format(balance);
  return (
    <>
      <Link
        href="/subscription#coins"
        aria-label={`${t.nav.coins}: ${full}. ${t.pay.guestUnsaved}`}
        title={t.pay.guestUnsaved}
        className="chip relative flex h-9 shrink-0 items-center gap-1 rounded-full pl-1 pr-2 text-[13.5px] font-medium tabular-nums text-fg hover:text-brand-soft min-[360px]:mr-1 min-[360px]:gap-1.5 min-[360px]:pr-2.5"
      >
        <SweetCoin size={24} />
        <span aria-hidden>{full}</span>
        {/* Not saved yet: the dot says so until they sign up. */}
        <span aria-hidden className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-amber-400 ring-2 ring-[var(--color-ink)]" />
      </Link>
      {remind && soon && (
        <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-24 z-[60] flex justify-center px-3 md:bottom-8">
          <div className="glass-strong pointer-events-auto flex w-full max-w-sm animate-toast-in items-start gap-3 rounded-2xl p-3 shadow-[0_24px_70px_-20px_rgb(255_70_150/0.55)]">
            <SweetCoin size={40} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[14px] leading-snug text-fg">{fmt(t.pay.guestExpiring, { n: soon.amount })}</p>
              <Link
                href="/signup?next=/subscription%23coins"
                onClick={close}
                className="mt-2 inline-flex h-9 items-center rounded-full bg-brand-strong px-4 text-[13.5px] font-semibold text-white hover:bg-brand-deep"
              >
                {t.pay.guestSignup}
              </Link>
            </div>
            <button type="button" onClick={close} aria-label={t.common.close} className="grid size-8 shrink-0 place-items-center rounded-full text-faint hover:bg-white/10 hover:text-fg">
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
